import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { ProjectFormData, ProjectStatus, statusOptions } from "./projectTypes";

interface ProjectDialogProps {
  open: boolean;
  saving: boolean;
  error: string | null;
  initialData: ProjectFormData;
  onClose: () => void;
  onSave: (data: ProjectFormData) => void;
}

export function ProjectDialog({
  open,
  saving,
  error,
  initialData,
  onClose,
  onSave,
}: ProjectDialogProps) {
  const [formData, setFormData] = useState<ProjectFormData>(initialData);

  useEffect(() => {
    if (open) {
      setFormData(initialData);
    }
  }, [initialData, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialData.title ? "Edit Project" : "Create Project"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Project title"
            value={formData.title}
            onChange={(event) =>
              setFormData((prev) => ({
                ...prev,
                title: event.target.value,
              }))
            }
            fullWidth
            required
          />
          <TextField
            label="Client email"
            type="email"
            value={formData.clientEmail}
            onChange={(event) =>
              setFormData((prev) => ({
                ...prev,
                clientEmail: event.target.value,
              }))
            }
            fullWidth
            required
            helperText="If they don't have an account, an invite email will be sent automatically."
          />
          <TextField
            label="Address"
            value={formData.address}
            onChange={(event) =>
              setFormData((prev) => ({
                ...prev,
                address: event.target.value,
              }))
            }
            fullWidth
          />
          <TextField
            select
            label="Status"
            value={formData.status}
            onChange={(event) =>
              setFormData((prev) => ({
                ...prev,
                status: event.target.value as ProjectStatus,
              }))
            }
            fullWidth
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Start date"
            type="date"
            value={formData.startDate}
            onChange={(event) =>
              setFormData((prev) => ({
                ...prev,
                startDate: event.target.value,
              }))
            }
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Estimated completion"
            type="date"
            value={formData.estimatedCompletionDate}
            onChange={(event) =>
              setFormData((prev) => ({
                ...prev,
                estimatedCompletionDate: event.target.value,
              }))
            }
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Budget"
            type="number"
            value={formData.budget}
            onChange={(event) =>
              setFormData((prev) => ({
                ...prev,
                budget: event.target.value,
              }))
            }
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(formData)}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
